<template>
  <div class="vision360">
    <!-- Cabecera fija: fuera de v-tabs-items, no cambia ni se recarga al cambiar de pestaña -->
    <Tutor360Header
      v-if="userInfo"
      :user-info="userInfo"
      :risk-signals="riskSignals"
      :pet-health="petHealth"
      :contracts="contracts"
      :profile-link="profileLink"
      class="mb-4"
      @edit-user="handleEditUser"
      @add-pet="handleAddPet"
      @add-co-tutor="openAddCoTutor"
      @manage-contracts="showContractsDialog = true"
      @copy-error="showErrorSnackbar('No se pudo copiar, cópialo manualmente')"
      @edit-note="openNoteModal"
      @reset-password="handleResetPassword"
      @force-logout="handleForceLogout"
      @suspicious="openSuspiciousModal"
      @blacklist="openBlacklistModal"
    />
    <SourceError360
      v-else-if="loadState.user.error"
      label="los datos del tutor"
      @retry="retry('user')"
    />

    <!-- Pestañas sincronizadas con ?tab= (patrón de Contratos.vue). `eager`: los datos se
         cargan una sola vez en el contenedor y cada pestaña queda montada. -->
    <v-tabs v-model="activeTab" show-arrows class="flex-grow-0 vision360-tabs">
      <v-tab
        v-for="tab in visibleTabs"
        :key="tab.value"
        :href="`#${tab.value}`"
        :data-test="`tab-${tab.value}`"
      >
        {{ tab.text }}
      </v-tab>
    </v-tabs>
    <v-divider class="mb-4" />

    <v-tabs-items v-model="activeTab" class="vision360-tabs-items">
      <v-tab-item v-if="isTabVisible('resumen')" value="resumen" eager>
        <Summary360Tab
          :show-memberships="access.memberships"
          :show-coverage="access.coverage"
          :details-plan="detailsPlan"
          :request-usage="requestUsage"
          :refund-statuses="refundStatuses"
          :coverage-usage="coverageUsage"
          :usage="summaryUsage"
          @view-usage="goToCoverage"
          @go-to="goToTab"
          @retry-usage="retry('activeUsage')"
        />
      </v-tab-item>

      <v-tab-item v-if="isTabVisible('mascotas')" value="mascotas" eager>
        <PetsPlans360Tab
          :pet-info="petInfo"
          :details-plan="detailsPlan"
          :sponsorship="sponsorship"
          :show-pets="access.memberships"
          :pets-with-membership="petIdsWithMembership"
          :token="token"
          :user-id="_getUserId()"
          :load-state="loadState"
          @add-pet="handleAddPet"
          @edit-pet="handleEditPet"
          @delete-pet="confirmDeletePet"
          @see-contract="seeContract"
          @update-contract="updateContract"
          @update-membership="handleEditMembership"
          @renew-membership="renewMembership"
          @change-sponsored-pet="openChangeSponsoredPet"
          @manage-sponsorship="openSponsorshipManager"
          @regenerate-payment-link="regeneratePaymentLink"
          @view-usage="goToCoverage"
          @retry="retry"
          @copy-error="showErrorSnackbar('No se pudo copiar, cópialo manualmente')"
        />
      </v-tab-item>

      <v-tab-item v-if="isTabVisible('coberturas')" value="coberturas" eager>
        <Coverage360Tab
          :memberships="detailsPlan"
          :token="token"
          :initial-membership-id="$route.query.membership || null"
          :coverage-usage="coverageUsage"
          :refunds-total="refundsTotal"
          :refunds-load-failed="refundsLoadFailed"
          :usage-load-failed="usageLoadFailed"
          :load-state="loadState"
          :pet-health="petHealth"
          @retry="retry"
        />
      </v-tab-item>

      <v-tab-item value="perfil" eager>
        <Profile360Tab
          :user-info="userInfo || {}"
          :risk-signals="riskSignals"
          :user-id="$route.params.pgId"
          :token="token"
          :load-state="loadState"
          @edit-user="handleEditUser"
          @add-co-tutor="openAddCoTutor"
          @edit-co-tutor="openEditCoTutor"
          @edit-note="openNoteModal"
          @blacklist="openBlacklistModal"
          @suspicious="openSuspiciousModal"
          @identity-validated="handleIdentityValidated"
          @reset-password="handleResetPassword"
          @force-logout="handleForceLogout"
          @retry="retry"
        />
      </v-tab-item>
    </v-tabs-items>

    <!-- Diálogos del tutor: los abren la cabecera, el menú «⋯» y Perfil y seguridad -->
    <PdfViewerDialog
      v-model="contractViewer.show"
      :src="contractViewer.src"
      :title="contractViewer.title"
      data-test="contract-viewer"
    />
    <v-dialog v-model="showAddPetDialog" max-width="1100px" scrollable data-test="add-pet-dialog">
      <v-card>
        <v-card-title class="headline d-flex justify-space-between align-center">
          Agregar mascota
          <v-btn icon aria-label="Cerrar" @click="showAddPetDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <AddPet
            v-if="showAddPetDialog && can('users:add-pet')"
            embedded
            :user-id="$route.params.pgId"
            @saved="onPetAdded"
            @cancel="showAddPetDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showContractsDialog" max-width="960px" data-test="contracts-dialog">
      <v-card>
        <v-card-title class="headline d-flex justify-space-between align-center">
          Contratos del tutor
          <v-btn icon aria-label="Cerrar" @click="showContractsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <UserCompaniesMaintainer
            v-if="showContractsDialog"
            embedded
            :user-id="$route.params.pgId"
            @loaded="contracts = $event"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <AddNoteModal
      v-if="userInfo"
      v-model="showNoteModal"
      :user-info="userInfo"
      :existing-note="userInfo.note || ''"
      :token="token"
      @note-saved="handleNoteSaved"
    />

    <BlacklistModal
      v-if="userInfo"
      v-model="showBlacklistModal"
      :user-info="userInfo"
      :token="token"
      :current-blacklist="riskSignals.blacklist"
      @saved="handleBlacklistSaved"
      @error="showErrorSnackbar"
    />

    <SuspiciousFlagModal
      v-if="userInfo"
      v-model="showSuspiciousModal"
      :rut="userInfo.rut"
      entity-type="user"
      :entity-label="userInfo.name || 'este usuario'"
      :can-flag="can('users:flag-suspicious')"
      @saved="handleSuspiciousSaved"
    />

    <AddCoTutorModal
      v-if="userInfo"
      v-model="showCoTutorModal"
      :user-info="userInfo"
      :existing-co-tutor="selectedCoTutor"
      :token="token"
      @co-tutor-saved="handleCoTutorSaved"
    />

    <v-dialog v-model="showUserMaintainerDialog" max-width="900px" @click:outside="closeUserMaintainerDialog" @keydown.esc="closeUserMaintainerDialog">
      <user-maintainer
        :user="selectedUserToEdit"
        @update-list="refreshUserData"
        @close-dialog="closeUserMaintainerDialog"
      />
    </v-dialog>

    <v-dialog v-model="showResetPasswordDialog" max-width="500px" :persistent="resetPasswordLoading" @keydown.esc="cancelResetPassword">
      <v-card>
        <v-card-title class="headline">Confirmar reinicio de contraseña</v-card-title>
        <v-card-text>
          <div v-if="!resetPasswordLoading">
            ¿Está seguro que desea reiniciar la contraseña de este usuario?
          </div>
          <div v-else class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="48" />
            <p class="mt-3">Procesando solicitud...</p>
          </div>
        </v-card-text>
        <v-card-actions v-if="!resetPasswordLoading">
          <v-spacer />
          <v-btn color="grey darken-1" text @click="cancelResetPassword">Cancelar</v-btn>
          <v-btn
            v-if="can('users:reset-password')"
            color="primary"
            text
            @click="confirmResetPassword"
          >Sí, reiniciar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showForceLogoutDialog" max-width="500px" :persistent="forceLogoutLoading" @keydown.esc="cancelForceLogout">
      <v-card>
        <v-card-title class="headline">Confirmar cierre de sesión forzado</v-card-title>
        <v-card-text>
          <div v-if="!forceLogoutLoading">
            ¿Está seguro que desea forzar el cierre de sesión de este usuario en todos sus dispositivos? Deberá volver a iniciar sesión y, hasta entonces, no recibirá notificaciones en ellos.
          </div>
          <div v-else class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="48" />
            <p class="mt-3">Procesando solicitud...</p>
          </div>
        </v-card-text>
        <v-card-actions v-if="!forceLogoutLoading">
          <v-spacer />
          <v-btn color="grey darken-1" text @click="cancelForceLogout">Cancelar</v-btn>
          <v-btn
            v-if="can('users:force-logout')"
            color="deep-orange"
            text
            @click="confirmForceLogout"
          >Sí, cerrar sesión</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showUpdateContractDialog" max-width="700px" :persistent="contractLoading" @keydown.esc="cancelContractUpdate">
      <v-card>
        <v-card-title class="headline">¡Atención, esto va a generar un nuevo contrato!</v-card-title>
        <v-card-text>
          <div v-if="!contractLoading">
            ¿Seguro que deseas generar un nuevo contrato para esta membresía?
          </div>
          <div v-else class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="48" />
            <p class="mt-3">Generando contrato...</p>
          </div>
        </v-card-text>
        <v-card-actions v-if="!contractLoading">
          <v-spacer />
          <v-btn text color="grey" @click="cancelContractUpdate">Cancelar</v-btn>
          <v-btn
            v-if="canAct('memberships:update')"
            text
            color="primary"
            @click="confirmContractUpdate"
          >Sí, generar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showEditPetDialog" max-width="800px" @keydown.esc="handlePetClosed" @click:outside="handlePetClosed">
        <pet-maintainer
            :pet="editingPetForMaintainer"
            @close-dialog="handlePetClosed"
            @pet-saved="handlePetSaved"
        />
    </v-dialog>

    <v-dialog v-model="showMembershipManagerDialog" max-width="900px"
        @keydown.esc="closeMembershipManager" @click:outside="closeMembershipManager">
        <membership-manager
            v-if="showMembershipManagerDialog && membershipManagerTarget"
            :membership="membershipManagerTarget"
            :token="_getToken()"
            @close-dialog="closeMembershipManager"
            @saved="onMembershipManagerSaved"
            @error="showErrorSnackbar"
        />
    </v-dialog>

    <v-dialog v-model="showDeletePetDialog" max-width="440px" :persistent="deletePetLoading" @keydown.esc="cancelDeletePet">
        <v-card>
            <v-card-title class="headline error--text">
                <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
                Eliminar mascota
            </v-card-title>
            <v-card-text class="pt-2">
                ¿Seguro que deseas eliminar a
                <strong>{{ petToDelete ? petToDelete.mascota : '' }}</strong>?
                Esta acción no se puede deshacer.
            </v-card-text>
            <v-card-actions class="justify-end">
                <v-btn text color="grey darken-1" :disabled="deletePetLoading" @click="cancelDeletePet">
                    Cancelar
                </v-btn>
                <v-btn
                    v-if="can('users:delete-pet')"
                    color="error"
                    :loading="deletePetLoading"
                    @click="doDeletePet"
                >
                    Eliminar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog
      v-model="showRenewDialog"
      max-width="980px"
      :persistent="renewSubmitting"
      @input="onRenewDialogInput"
      @click:outside="!renewSubmitting && closeRenewDialog()"
      @keydown.esc="!renewSubmitting && closeRenewDialog()"
    >
      <renew-membership-modal
        v-if="showRenewDialog && membershipToRenew"
        :membership="membershipToRenew"
        :user-id="$route.params.pgId"
        @close-dialog="closeRenewDialog"
        @renewed="handleMembershipRenewed"
        @submitting="renewSubmitting = $event"
      />
    </v-dialog>

    <!-- Persistent para que el link de pago no se pierda por clic afuera o ESC -->
    <v-dialog v-model="showSponsorshipManagerDialog" max-width="600px" :persistent="sponsorshipManagerSubmitting">
      <sponsorship-manager
        v-if="showSponsorshipManagerDialog && sponsorshipToManage"
        :sponsorship="sponsorshipToManage"
        @close-dialog="closeSponsorshipManager"
        @changed="handleSponsorshipStatusChanged"
        @submitting="sponsorshipManagerSubmitting = $event"
      />
    </v-dialog>

    <v-dialog
      v-model="showChangeSponsoredPetDialog"
      max-width="560px"
      :persistent="changeSponsoredPetSubmitting"
    >
      <change-sponsored-pet-modal
        v-if="showChangeSponsoredPetDialog && sponsorshipToChange"
        :sponsorship="sponsorshipToChange"
        @close-dialog="closeChangeSponsoredPet"
        @changed="handleSponsoredPetChanged"
        @submitting="changeSponsoredPetSubmitting = $event"
      />
    </v-dialog>

    <v-dialog v-model="showRenewalResultDialog" max-width="600px">
      <v-card v-if="renewalResult">
        <v-card-title
          class="headline"
          :class="renewalResult.paymentLink
            ? 'primary--text'
            : 'orange--text text--darken-2'"
        >
          {{ renewalResultTitle }}
        </v-card-title>
        <v-card-text>
          <template v-if="renewalResult.paymentLink">
            <v-text-field
              :value="renewalResult.paymentLink"
              label="Link de pago"
              outlined
              dense
              readonly
              hide-details="auto"
              class="mb-3"
            >
              <template v-slot:append>
                <v-btn small text color="primary" class="mt-n1" @click="copyRenewalPaymentLink">
                  <v-icon small left>mdi-content-copy</v-icon>
                  {{ renewalLinkCopied ? 'Copiado' : 'Copiar' }}
                </v-btn>
              </template>
            </v-text-field>
            <v-alert type="info" text dense>
              La membresía quedó pendiente de pago. Envía este link al usuario; cuando
              pague, actívala desde "Cambiar estado de membresía".
            </v-alert>
          </template>

          <v-alert
            v-else-if="renewalResult.intervalChanged && renewalResultMpManaged"
            type="error"
            text
            dense
          >
            El intervalo de pago cambió, pero el link de pago NO se pudo generar.
            Gestiona el cobro desde el dashboard de Mercado Pago o reintenta la operación.
          </v-alert>

          <template v-if="renewalWarnings.length > 0">
            <p>Ocurrieron las siguientes advertencias:</p>
            <v-alert
              v-for="(warning, index) in renewalWarnings"
              :key="index"
              type="warning"
              text
              dense
            >
              <strong v-if="warning.code">{{ warning.code }}: </strong>
              {{ warning.message }}
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="closeRenewalResultDialog">Entendido</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Aviso/preview antes de regenerar el link de pago: la acción cancela la
         suscripción anterior de MercadoPago, así que se confirma explícitamente -->
    <v-dialog
      v-model="showRegenerateLinkDialog"
      max-width="600px"
      :persistent="regenerateLinkSubmitting"
    >
      <v-card v-if="membershipToRegenerate">
        <v-card-title class="headline primary--text">
          Regenerar link de pago
        </v-card-title>
        <v-card-text>
          <v-sheet class="grey lighten-4 rounded pa-3 mb-4">
            <v-row dense>
              <v-col cols="7" class="py-1">
                <div class="text-caption grey--text">Mascota</div>
                <div class="font-weight-medium">{{ membershipToRegenerate.name || '—' }}</div>
              </v-col>
              <v-col cols="5" class="py-1">
                <div class="text-caption grey--text">Referencia</div>
                <div class="font-weight-medium">{{ membershipToRegenerate.ref || '—' }}</div>
              </v-col>
              <v-col cols="12" class="py-1">
                <div class="text-caption grey--text">Plan</div>
                <div class="font-weight-medium">{{ membershipToRegenerate.planName || '—' }}</div>
              </v-col>
            </v-row>
          </v-sheet>

          <div v-if="paymentLinkPreviewLoading" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>

          <template v-else-if="paymentLinkPreview">
            <v-alert
              v-if="!paymentLinkPreview.hasPreviousPayment && !paymentLinkPreview.isActive"
              type="warning"
              text
              dense
              class="mb-0"
            >
              Se generará un <strong>nuevo link de pago</strong> de MercadoPago.
              Envíaselo al usuario y, cuando pague, activa la membresía desde
              "Cambiar estado de membresía".
            </v-alert>

            <v-alert
              v-if="paymentLinkPreview.isActive"
              type="warning"
              text
              dense
              class="mb-3"
            >
              Esta membresía está <strong>activa</strong>: al generar el nuevo link
              quedará <strong>pendiente de pago</strong> hasta que el usuario pague,
              sin importar qué elijas abajo sobre la suscripción actual.
            </v-alert>

            <template v-if="paymentLinkPreview.hasPreviousPayment">
              <v-alert type="info" text dense class="mb-3">
                Esta mascota ya tiene <strong>pagos anteriores</strong>: probablemente
                tiene una suscripción activa en MercadoPago.
              </v-alert>
              <div class="text-body-2 mb-1">¿Qué hacer con la suscripción actual?</div>
              <v-radio-group v-model="regenerateLinkAction" class="mt-0" hide-details>
                <v-radio value="keep">
                  <template v-slot:label>
                    <div>
                      <strong>Mantener</strong> la suscripción actual en MercadoPago
                      (no se cancela) y generar un link adicional.
                    </div>
                  </template>
                </v-radio>
                <v-radio value="cancel">
                  <template v-slot:label>
                    <div>
                      <strong>Cancelar</strong> la suscripción actual de inmediato en
                      MercadoPago.
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </template>
          </template>

          <v-alert v-if="regenerateLinkError" type="error" text dense class="mt-4 mb-0">
            <span v-html="regenerateLinkErrorHtml"></span>
            <div v-if="regenerateLinkError.detail || regenerateLinkError.code" class="mt-2">
              <a
                class="text-caption error--text font-weight-medium"
                style="text-decoration: underline"
                @click="showRegenerateErrorDetail = !showRegenerateErrorDetail"
              >
                {{ showRegenerateErrorDetail ? 'Ocultar' : 'Ver' }} detalle técnico
              </a>
              <div
                v-if="showRegenerateErrorDetail"
                class="text-caption grey--text text--darken-2 mt-1"
                style="word-break: break-word"
              >
                <div v-if="regenerateLinkError.code">Código: {{ regenerateLinkError.code }}</div>
                <div v-if="regenerateLinkError.detail">{{ regenerateLinkError.detail }}</div>
              </div>
            </div>
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end">
          <v-btn
            text
            color="grey darken-1"
            :disabled="regenerateLinkSubmitting"
            @click="closeRegenerateLinkDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            v-if="can('users:regenerate-payment-link')"
            color="primary"
            :loading="regenerateLinkSubmitting"
            :disabled="paymentLinkPreviewLoading"
            @click="confirmRegeneratePaymentLink"
          >
            Generar link
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">
          Entendido
        </v-btn>
      </template>
    </v-snackbar>
    <Overlay :value="showOverlay" />
  </div>
</template>

<script>
import Tutor360Header from '../components/Tutor360Header.vue';
import Summary360Tab from '../components/Summary360Tab.vue';
import PetsPlans360Tab from '../components/PetsPlans360Tab.vue';
import Coverage360Tab from '../components/Coverage360Tab.vue';
import Profile360Tab from '../components/Profile360Tab.vue';
import SourceError360 from '../components/SourceError360.vue';
import AddNoteModal from '../components/AddNoteModal.vue';
import BlacklistModal from '../components/BlacklistModal.vue';
import SuspiciousFlagModal from '../components/SuspiciousFlagModal.vue';
import AddCoTutorModal from '../components/AddCoTutorModal.vue';
import UserMaintainer from '../components/UserMaintainer.vue';
import UserCompaniesMaintainer from '../components/UserCompaniesMaintainer.vue';
import AddPet from './AddPet.vue';
import PdfViewerDialog from '../components/PdfViewerDialog.vue';
import Api360Service from '../services/360.service.js';
import PetMaintainer from '../components/PetMaintainer.vue';
import RenewMembershipModal from '../components/RenewMembershipModal.vue';
import ChangeSponsoredPetModal from '../components/ChangeSponsoredPetModal.vue';
import SponsorshipManager from '../components/SponsorshipManager.vue';
import MembershipManager from '../components/MembershipManager.vue';
import MembershipsService from '../services/memberships.service';
import PetsService from '../services/pets.service';
import SuspiciousFlagsService from '../services/suspicious-flags.service';
import CompaniesService from '../services/companie.service.js';
import Overlay from '../components/Overlay.vue';
import { dataMixin } from '@/mixins/dataMixin';
import { petMixin } from '@/mixins/petMixin';
import { utilsMixin } from '@/mixins/utilsMixin';
import { refundStatusColorsMixin } from '@/mixins/refundStatusColorsMixin';
import { buildCoverageUsageRows } from '@/utils/coverage-usage.utils';
import { resolveTab, tabQuery } from '@/utils/vision360.utils';
import { membershipActions, representativeIds } from '@/utils/membership-actions.utils';
import { can, canAct } from '@/utils/permissions';

const TABS = [
  { value: 'resumen', text: 'Resumen' },
  { value: 'mascotas', text: 'Mascotas y planes' },
  { value: 'coberturas', text: 'Coberturas y servicios' },
  { value: 'perfil', text: 'Perfil y seguridad' }
];

// Fuente de datos → método que la carga (FR-022): cada una se pide y se reintenta por separado.
const SOURCE_LOADERS = {
  user: 'loadUser',
  pets: 'fetchPetData',
  petHealth: 'fetchPetHealth',
  memberships: 'fetchMembershipData',
  refunds: 'fetchRefundsData',
  usages: 'fetchServiceUsage',
  sponsorship: 'fetchSponsorship',
  activeUsage: 'fetchActiveMembershipsUsage'
};

// Tag que exige cada fuente (spec 132): sin él el backend responde 403, así que ni se pide.
const SOURCE_PERMISSIONS = {
  pets: 'users:view-memberships',
  petHealth: 'users:view-memberships',
  memberships: 'users:view-memberships',
  refunds: 'users:view-coverage',
  usages: 'users:view-coverage',
  activeUsage: 'users:view-coverage',
  sponsorship: 'users:view-sponsorship'
};

// Pestañas que necesitan algún dato además del tutor; «Perfil y seguridad» siempre se ve.
const TAB_REQUIREMENTS = {
  resumen: (access) => access.memberships || access.coverage,
  mascotas: (access) => access.memberships || access.sponsorship,
  coberturas: (access) => access.coverage
};

// Fuentes del Promise.allSettled principal; el uso de membresías vigentes va aparte.
const MAIN_SOURCES = ['pets', 'petHealth', 'refunds', 'usages', 'sponsorship', 'memberships'];

const initialLoadState = () =>
  Object.keys(SOURCE_LOADERS).reduce((state, source) => {
    state[source] = { loading: false, error: false };
    return state;
  }, {});

// Cambiar a la pestaña en la que ya se está no es un error para el usuario.
const isRedundantNavigation = (error) =>
  Boolean(error) && (error.name === 'NavigationDuplicated' || /redundant navigation/i.test(error.message || ''));

export default {
  name: 'Vision360',
  mixins: [dataMixin, petMixin, utilsMixin, refundStatusColorsMixin],
  components: {
    Tutor360Header,
    Summary360Tab,
    PetsPlans360Tab,
    Coverage360Tab,
    Profile360Tab,
    SourceError360,
    AddNoteModal,
    BlacklistModal,
    SuspiciousFlagModal,
    AddCoTutorModal,
    UserMaintainer,
    UserCompaniesMaintainer,
    AddPet,
    PdfViewerDialog,
    PetMaintainer,
    RenewMembershipModal,
    ChangeSponsoredPetModal,
    SponsorshipManager,
    MembershipManager,
    Overlay
  },

  data() {
    return {
      tabs: TABS,
      // Bloques del perfil que este admin puede ver (spec 132, 2026-11-03).
      access: {
        memberships: can('users:view-memberships'),
        coverage: can('users:view-coverage'),
        sponsorship: can('users:view-sponsorship'),
        security: can('users:view-security')
      },
      loadState: initialLoadState(),
      // Asociaciones empresa/contrato del tutor, para los chips de la cabecera.
      contracts: [],
      showContractsDialog: false,
      showAddPetDialog: false,
      contractViewer: { show: false, src: '', title: '' },
      // Respuesta de GET /memberships/360/active-service-usage/:pgId ({ uf, memberships }).
      activeMembershipsUsage: null,
      // Respuesta de GET /pets/360/health-status/:pgId ({ hasOverdueVaccines, hasOverdueDeworming, pets }).
      petHealth: null,
      riskSignals: { blacklist: null, suspicious: null },

      showNoteModal: false,
      showBlacklistModal: false,
      showSuspiciousModal: false,
      showCoTutorModal: false,
      selectedCoTutor: null,

      userData: null,
      showOverlay: false,
      loading: false,
      error: null,
      userInfo: {},
      petInfo: [],
      stats: {},
      detailsPlan: [],
      requestUsage: [],
      refundsTotal: 0,
      // Usos que no son reembolso (operativo, PetMed, ...); se unen a requestUsage en coverageUsage.
      serviceUsage: [],
      refundsLoadFailed: false,
      usageLoadFailed: false,
      sponsorship: [],
      
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      
      
      showUpdateContractDialog: false,
      contractLoading: false,
      membershipToUpdate: null,
      
      showUserMaintainerDialog: false,
      selectedUserToEdit: null,
      
      showResetPasswordDialog: false,
      resetPasswordUserId: null,
      resetPasswordLoading: false,

      showForceLogoutDialog: false,
      forceLogoutUserId: null,
      forceLogoutLoading: false,

      showRenewDialog: false,
      showChangeSponsoredPetDialog: false,
      sponsorshipToChange: null,
      changeSponsoredPetSubmitting: false,
      showSponsorshipManagerDialog: false,
      sponsorshipToManage: null,
      sponsorshipManagerSubmitting: false,
      membershipToRenew: null,
      renewSubmitting: false,
      showRenewalResultDialog: false,
      renewalResult: null,
      renewalWarnings: [],
      renewalLinkCopied: false,

      showRegenerateLinkDialog: false,
      membershipToRegenerate: null,
      regenerateLinkSubmitting: false,
      regenerateLinkError: null,
      showRegenerateErrorDetail: false,
      paymentLinkPreviewLoading: false,
      paymentLinkPreview: null,
      regenerateLinkAction: 'keep',

      breeds: [],
      loadingBreeds: false,
      showEditPetDialog: false,
      editingPet: {
        name: '',
        breed: '',
        originalName: '',
        membershipId: null,
        species: ''
      },
      editPetLoading: false,
      showMembershipManagerDialog: false,
      membershipManagerTarget: null,
      showDeletePetDialog: false,
      petToDelete: null,
      deletePetLoading: false,

      birthdateMenu: false,
      petData: { birthdate: null }
    }
  },
  
  async created() {
    await this.initializeData();
  },

  watch: {
    // Las señales de riesgo dependen del RUT: se cargan al llegar el tutor (y si cambia al editarlo).
    'userInfo.rut': {
      handler(rut) {
        if (rut && this.access.security) this.loadRiskSignals(rut);
      },
      immediate: true
    }
  },

  computed: {
    // Pestaña activa en ?tab=; `replace` para no llenar el historial del navegador.
    visibleTabs() {
      return this.tabs.filter((tab) => this.isTabVisible(tab.value));
    },

    // Una pestaña oculta por permisos (p. ej. ?tab=coberturas sin el tag) cae en la primera visible.
    activeTab: {
      get() {
        const tab = resolveTab(this.$route.query);
        return this.isTabVisible(tab) ? tab : this.visibleTabs[0].value;
      },
      set(tab) {
        if (tab === this.activeTab) return;
        const membership = tab === 'coberturas' ? this.$route.query.membership : null;
        this.replaceQuery(tabQuery(tab, { membership }));
      }
    },

    token() {
      return this._getToken();
    },

    // Estado del resumen de uso de las membresías vigentes para la pestaña Resumen.
    summaryUsage() {
      return {
        loading: this.loadState.activeUsage.loading,
        error: this.loadState.activeUsage.error,
        data: this.activeMembershipsUsage
      };
    },

    // Reembolsos y demás usos del usuario en una sola lista, ordenada por fecha. Es calculada
    // para que renombrar una mascota (updatePetDataLocally) se vea en ambas fuentes.
    // URL absoluta de esta 360 (para «Copiar datos» de la cabecera).
    profileLink() {
      const { href } = this.$router.resolve({ name: 'Vision360', params: { pgId: this.$route.params.pgId } });
      return new URL(href, window.location.origin).href;
    },

    coverageUsage() {
      return buildCoverageUsageRows(this.requestUsage, this.serviceUsage);
    },

    // Ids de mascotas que tienen al menos una membresía: se usa para bloquear la
    // eliminación de la mascota desde la sección de mascotas.
    petIdsWithMembership() {
      return this.detailsPlan
        .map(m => m.petId)
        .filter(id => id != null);
    },

    renewalResultTitle() {
      if (this.renewalResult && this.renewalResult.regenerated) {
        return 'Link de pago regenerado';
      }
      if (this.renewalResult && this.renewalResult.paymentLink) {
        return 'Renovación realizada — link de pago generado';
      }
      return 'Renovación realizada con advertencias';
    },

    // Renderiza el error del backend soportando negritas Markdown (**texto**).
    // Se escapa el HTML antes de convertir, así el v-html es seguro.
    regenerateLinkErrorHtml() {
      if (!this.regenerateLinkError || !this.regenerateLinkError.message) return '';
      const escaped = String(this.regenerateLinkError.message)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    },

    // true si la renovación operó con MercadoPago; respuestas antiguas sin el
    // campo `mpManaged` se tratan como gestionadas por MP (comportamiento previo)
    renewalResultMpManaged() {
      return Boolean(this.renewalResult) && this.renewalResult.mpManaged !== false;
    },


    editingPetForMaintainer() {
        if (!this.editingPet.id) {
            return null;
        }
        const result = {
            id: this.editingPet.id,
            name: this.editingPet.name,
            species: this.editingPet.species,
            sex: this.editingPet.sex || 'male',
            birthdate: this.petData.birthdate,
            breed: this.editingPet.breedId ? {
            id: this.editingPet.breedId,
            name: this.editingPet.breed
            } : null,
            breedId: this.editingPet.breedId,
            microchip: this.editingPet.microchip || '',
            hasMicrochip: this.editingPet.hasMicrochip || false,
            isPurebred: this.editingPet.isPurebred || false,
            visible: this.editingPet.visible !== false
        };
      return result;
    }
  },

  methods: {
    can,
    canAct,
    // Carga inicial: el tutor primero (como antes) y luego el resto en paralelo; cada fuente
    // marca su propio estado, así la falla de una no bloquea las demás (FR-022). El uso de
    // membresías vigentes y el catálogo de estados de reembolso van fuera del allSettled.
    async initializeData() {
      this.loading = true;
      this.error = null;

      await this.loadSource('user');
      this.loadSource('activeUsage');
      this.loadContracts();
      this.loadRefundStatusColors().catch(() => {
        // Sin catálogo, pendingRefundsCount cuenta todo reembolso como pendiente (regla R5).
        this.refundStatuses = [];
      });
      await Promise.allSettled(MAIN_SOURCES.map(source => this.loadSource(source)));
      this.loading = false;
      this.openRenewalFromQuery();
    },

    // «Renovar» desde Vencimientos llega con ?renew=<membershipId>: abre «Mascotas y planes» con
    // el modal de renovación. Se quita de la URL para que recargar no lo vuelva a abrir.
    openRenewalFromQuery() {
      const membershipId = this.$route.query.renew;
      if (!membershipId) return;
      this.replaceQuery(tabQuery('mascotas'));
      const membership = this.detailsPlan.find(m => String(m.id) === String(membershipId));
      const renewable = membership
        && can('users:renew-memberships')
        && membershipActions(membership, representativeIds(this.detailsPlan)).includes('renewMembership');
      if (!renewable) {
        this.showErrorSnackbar('Esta membresía ya no se puede renovar');
        return;
      }
      this.renewMembership(membership);
    },

    // Pide una fuente y deja su resultado en loadState[source]. Nunca rechaza.
    async loadSource(source) {
      const permission = SOURCE_PERMISSIONS[source];
      if (permission && !can(permission)) return;
      const state = this.loadState[source];
      state.loading = true;
      state.error = false;
      try {
        await this[SOURCE_LOADERS[source]]();
      } catch (error) {
        state.error = true;
      } finally {
        state.loading = false;
      }
    },

    // Reintenta solo la fuente indicada (botón «Reintentar» de SourceError360).
    retry(source) {
      if (!SOURCE_LOADERS[source]) return Promise.resolve();
      return this.loadSource(source);
    },

    // fetchUserData no lanza: deja userData en null si falla. Se traduce a error de la fuente.
    async loadUser() {
      await this.fetchUserData();
      if (!this.userData) throw this.error || new Error('No se pudo cargar el usuario');
    },

    // Los chips son informativos: si la carga falla, la cabecera simplemente no los muestra.
    async loadContracts() {
      const res = await CompaniesService.getUserCompanyAccesses(this.$route.params.pgId);
      this.contracts = res.status === 'ok' && Array.isArray(res.data) ? res.data : [];
    },

    async fetchPetHealth() {
      try {
        this.petHealth = await Api360Service.getPetHealthStatus(this._getUserId(), this._getToken());
      } catch (error) {
        this.petHealth = null;
        throw error;
      }
    },

    async fetchActiveMembershipsUsage() {
      try {
        this.activeMembershipsUsage = await Api360Service.getActiveMembershipsServiceUsage(
          this._getUserId(),
          this._getToken()
        );
      } catch (error) {
        this.activeMembershipsUsage = null;
        throw error;
      }
    },

    // Refresca las membresías tras una acción (renovar, regenerar link, contrato, gestor) y, en
    // paralelo, el resumen de uso: el estado de una membresía puede haber dejado de ser Activo.
    // Lanza si falla la recarga de membresías, como el fetchMembershipData que reemplaza.
    async refreshMembershipData() {
      this.loadSource('activeUsage');
      await this.fetchMembershipData();
      this.loadState.memberships.error = false;
    },

    replaceQuery(query) {
      return this.$router.replace({ query }).catch(error => {
        if (!isRedundantNavigation(error)) this.showErrorSnackbar('No se pudo cambiar de pestaña');
      });
    },

    goToTab(tab) {
      this.activeTab = tab;
    },

    isTabVisible(tab) {
      const requirement = TAB_REQUIREMENTS[tab];
      return !requirement || requirement(this.access);
    },

    // «Ver uso»: abre Coberturas y servicios con esa membresía preseleccionada (?membership=).
    goToCoverage(membershipId) {
      return this.replaceQuery(tabQuery('coberturas', { membership: membershipId }));
    },

    // ─── Señales de riesgo y diálogos del tutor ───────────────────────────

    loadRiskSignals(rut) {
      return Promise.all([this.loadBlacklistStatus(rut), this.loadSuspiciousStatus(rut)]);
    },

    async loadBlacklistStatus(rut) {
      try {
        const data = await Api360Service.getBlacklist(rut, this._getToken());
        this.riskSignals.blacklist = data?.isBlacklisted
          ? { reason: data.blacklist?.reason || 'Sin motivo registrado' }
          : null;
      } catch (error) {
        // Sin dato no se muestra el chip; el modal sigue permitiendo revisarlo.
        this.riskSignals.blacklist = null;
      }
    },

    async loadSuspiciousStatus(rut) {
      try {
        const result = await SuspiciousFlagsService.lookup(rut, 'user');
        this.riskSignals.suspicious = result.status === 'ok' && result.data.isSuspicious
          ? result.data.suspiciousFlag
          : null;
      } catch (error) {
        this.riskSignals.suspicious = null;
      }
    },

    openNoteModal() {
      this.showNoteModal = true;
    },

    openBlacklistModal() {
      this.showBlacklistModal = true;
    },

    openSuspiciousModal() {
      this.showSuspiciousModal = true;
    },

    openAddCoTutor() {
      this.selectedCoTutor = null;
      this.showCoTutorModal = true;
    },

    openEditCoTutor(coTutor) {
      this.selectedCoTutor = coTutor;
      this.showCoTutorModal = true;
    },

    async handleBlacklistSaved() {
      if (this.userInfo?.rut) await this.loadBlacklistStatus(this.userInfo.rut);
      this.showSuccessSnackbar('Lista negra actualizada correctamente');
    },

    handleIdentityValidated(date) {
      if (!this.userInfo) return;
      this.userInfo = { ...this.userInfo, identityValidatedAt: date || new Date().toISOString() };
    },

    updateContract(item) {
      this.membershipToUpdate = item;
      this.showUpdateContractDialog = true;
    },

    cancelContractUpdate() {
      this.showUpdateContractDialog = false;
      this.membershipToUpdate = null;
    },

    async confirmContractUpdate() {
        if (!this.membershipToUpdate) return;
        this.contractLoading = true;
        try {
            const token = this._getToken();
            await Api360Service.updateMembershipContract(this.membershipToUpdate.id, token);  
            this.showSuccessSnackbar('Contrato actualizado correctamente');

            await Promise.allSettled([
            this.loadSource('user'),
            this.refreshMembershipData()
            ]);

        } catch (err) {
            this.showErrorSnackbar('No se pudo actualizar el contrato');
        } finally {
            this.contractLoading = false;
            this.showUpdateContractDialog = false;
            this.membershipToUpdate = null;
        }
    },

    // Abre el contrato en el visor PDF del admin (con enlace para abrirlo en otra pestaña).
    seeContract(item) {
      if (item?.contractUrl) {
        this.contractViewer = { show: true, src: item.contractUrl, title: `Contrato · ${item.planName || 'Membresía'}` };
      } else {
        this.showErrorSnackbar('No hay contrato disponible para esta membresía');
      }
    },

    handleEditUser() {
      if (!this.userData) return;
      this.selectedUserToEdit = this.prepareUserForEdit(this.userData);
      this.showUserMaintainerDialog = true;
    },

    prepareUserForEdit(userData) {
      const parsedAddress = this.parseAddress(userData.address);
      const formattedBirthdate = this.parseBirthdate(userData.birthdate);
      const [firstName, lastName] = this.splitName(userData.name);
      return {
        pgId: this.$route.params.pgId,
        role: userData.role || 'user',
        email: userData.email,
        name: firstName,
        surname: lastName || '',
        commune: parsedAddress.commune,
        region: parsedAddress.region,
        address: {
          region: parsedAddress.region,
          commune: parsedAddress.commune,
          street: parsedAddress.street,
          number: parsedAddress.number,
          extra: parsedAddress.extra || ''
        },
        birthday: formattedBirthdate,
        rut: userData.identifier,
        country: userData.country,
        phone: userData.phone,
        isVip: userData.isVip || false
      };
    },

    // Nota guardada desde la cabecera o Perfil: se actualiza el tutor del contenedor (FR-021),
    // con un objeto nuevo para que la cabecera y la pestaña se rendericen sin recargar.
    handleNoteSaved(data) {
      const note = (data && data.note) || '';
      if (this.userInfo) this.userInfo = { ...this.userInfo, note };
      if (this.userData) this.userData = { ...this.userData, note };
      this.showNoteModal = false;
      this.showSuccessSnackbar('Nota guardada correctamente');
    },

    async handleSuspiciousSaved() {
      if (this.userInfo?.rut) await this.loadSuspiciousStatus(this.userInfo.rut);
      this.showSuccessSnackbar('Sospecha actualizada correctamente');
    },

    handleCoTutorSaved(coTutor) {
      if (this.userInfo && coTutor) {
        const coTutors = [...(this.userInfo.coTutors || [])];
        const index = coTutors.findIndex(ct => ct.id === coTutor.id);
        if (index >= 0) {
          coTutors.splice(index, 1, coTutor);
        } else {
          coTutors.push(coTutor);
        }
        this.userInfo = { ...this.userInfo, coTutors };
      }
      this.showCoTutorModal = false;
    },

    closeUserMaintainerDialog() {
      this.showUserMaintainerDialog = false;
      this.selectedUserToEdit = null;
      this.refreshUserData();
    },

    async refreshUserData() {
      this.loading = true;
      this.error = null;
      await this.loadSource('user');
      this.loading = false;
    },

    handleAddPet() {
      this.showAddPetDialog = true;
    },

    // Mascota y membresía creadas desde el diálogo: se cierra y se recargan ambas fuentes.
    async onPetAdded() {
      this.showAddPetDialog = false;
      this.showSuccessSnackbar('Mascota y membresía agregadas');
      await Promise.allSettled([
        this.loadSource('pets'),
        this.loadSource('petHealth'),
        this.refreshMembershipData()
      ]);
    },

    handleResetPassword() {
      this.resetPasswordUserId = this.$route.params.pgId;
      this.showResetPasswordDialog = true;
    },

    async confirmResetPassword() {
      this.resetPasswordLoading = true;
      try {
        const token = this._getToken();
        await Api360Service.resetUserPassword(this.resetPasswordUserId, token);
        this.showSuccessSnackbar('Contraseña reestablecida correctamente');
      } catch (error) {
        this.showErrorSnackbar('Error al restablecer la contraseña');
      } finally {
        this.resetPasswordLoading = false;
        this.resetUIState();
      }
    },

    cancelResetPassword() {
      this.resetUIState();
    },

    resetUIState() {
      this.showResetPasswordDialog = false;
      this.resetPasswordUserId = null;
    },

    handleForceLogout() {
      this.forceLogoutUserId = this.$route.params.pgId;
      this.showForceLogoutDialog = true;
    },

    async confirmForceLogout() {
      this.forceLogoutLoading = true;
      try {
        const token = this._getToken();
        await Api360Service.forceLogout(this.forceLogoutUserId, token);
        // El backend borró sus push tokens: la tarjeta «Accesos» queda sin dispositivos.
        if (this.userInfo) this.userInfo = { ...this.userInfo, devices: [] };
        this.showSuccessSnackbar('Sesión cerrada correctamente en todos los dispositivos');
      } catch (error) {
        this.showErrorSnackbar('Error al forzar el cierre de sesión');
      } finally {
        this.forceLogoutLoading = false;
        this.resetForceLogoutUIState();
      }
    },

    cancelForceLogout() {
      this.resetForceLogoutUIState();
    },

    resetForceLogoutUIState() {
      this.showForceLogoutDialog = false;
      this.forceLogoutUserId = null;
    },

    handlePetSaved() {
      this.showSuccessSnackbar('Mascota actualizada correctamente');
    },

    async handlePetClosed() {
      this.showEditPetDialog = false;
      try {
        this.showOverlay = true;
        await Promise.allSettled([
          this.loadSource('pets'),
          this.loadSource('petHealth'),
          this.refreshMembershipData()
        ]);
      } catch (error) {
        this.showErrorSnackbar('Error al actualizar la vista');
      } finally {
        this.showOverlay = false;
      }
    },

    // Edición de mascota desde la sección de mascotas (reutiliza el setup del mixin).
    handleEditPet(pet) {
        this.handleUpdateMembership({
            petId: pet.id,
            name: pet.mascota || pet.name,
            species: pet.species
        });
    },

    // Abre el gestor de membresía (datos + estado) desde la tabla. El propio
    // componente MembershipManager trae los datos completos y el historial.
    handleEditMembership(item) {
        if (!item || !item.id) {
            this.showErrorSnackbar('No se encontró la membresía');
            return;
        }
        this.membershipManagerTarget = item;
        this.showMembershipManagerDialog = true;
    },

    // El gestor guardó (datos y/o estado): avisa y refresca al cerrar.
    onMembershipManagerSaved() {
        this.showSuccessSnackbar('Cambios guardados');
        this.closeMembershipManager();
    },

    // Elimina una mascota sin membresía asociada (el backend valida).
    // Abre el diálogo de confirmación para eliminar una mascota.
    confirmDeletePet(pet) {
        if (!pet || !pet.id) {
            this.showErrorSnackbar('No se pudo identificar la mascota');
            return;
        }
        this.petToDelete = pet;
        this.showDeletePetDialog = true;
    },

    cancelDeletePet() {
        this.showDeletePetDialog = false;
        this.petToDelete = null;
    },

    // Ejecuta la eliminación tras la confirmación del diálogo.
    async doDeletePet() {
        const petId = this.petToDelete && this.petToDelete.id;
        if (!petId) return;
        try {
            this.deletePetLoading = true;
            const res = await PetsService.deletePet(petId);
            if (res.status !== 'ok') {
                // PetsService ya trae el motivo del backend; p. ej. el 409 «No se puede eliminar
                // la mascota porque es paciente de una clínica» (FR-014).
                this.showErrorSnackbar(res.message || 'No se pudo eliminar la mascota');
                return;
            }
            this.showDeletePetDialog = false;
            this.petToDelete = null;
            this.showSuccessSnackbar('Mascota eliminada');
            await Promise.allSettled([
                this.loadSource('pets'),
                this.loadSource('petHealth'),
                this.refreshMembershipData()
            ]);
        } catch (error) {
            this.showErrorSnackbar('Error al eliminar la mascota');
        } finally {
            this.deletePetLoading = false;
        }
    },

    async closeMembershipManager() {
        this.showMembershipManagerDialog = false;
        this.membershipManagerTarget = null;
        try {
            this.showOverlay = true;
            await this.refreshMembershipData();
        } catch (error) {
            this.showErrorSnackbar('Error al actualizar la vista');
        } finally {
            this.showOverlay = false;
        }
    },

    renewMembership(membership) {
      if (!membership?.id) {
        this.showErrorSnackbar('Membresía no encontrada');
        return;
      }
      this.membershipToRenew = membership;
      this.showRenewDialog = true;
    },

    openChangeSponsoredPet(sponsorship) {
      this.sponsorshipToChange = sponsorship;
      this.showChangeSponsoredPetDialog = true;
    },

    openSponsorshipManager(sponsorship) {
      this.sponsorshipToManage = sponsorship;
      this.showSponsorshipManagerDialog = true;
    },

    closeSponsorshipManager() {
      this.showSponsorshipManagerDialog = false;
      this.sponsorshipToManage = null;
      this.sponsorshipManagerSubmitting = false;
    },

    async handleSponsorshipStatusChanged() {
      this.closeSponsorshipManager();
      this.showSuccessSnackbar('Estado del apadrinamiento actualizado');
      await this.loadSource('sponsorship');
    },

    closeChangeSponsoredPet() {
      this.showChangeSponsoredPetDialog = false;
      this.sponsorshipToChange = null;
      this.changeSponsoredPetSubmitting = false;
    },

    // Tras el cambio se recargan solo los apadrinamientos: el nombre de la mascota es lo que cambia.
    async handleSponsoredPetChanged() {
      this.closeChangeSponsoredPet();
      this.showSuccessSnackbar('Mascota apadrinada actualizada');
      await this.loadSource('sponsorship');
    },

    closeRenewDialog() {
      this.showRenewDialog = false;
      this.membershipToRenew = null;
      this.renewSubmitting = false;
    },

    // Abre el aviso/preview antes de regenerar el link: primero consulta si la
    // mascota ya tiene pagos anteriores, para decidir si ofrece la opción de
    // mantener/cancelar la suscripción actual. No genera nada todavía; la acción
    // real ocurre en confirmRegeneratePaymentLink.
    async regeneratePaymentLink(membership) {
      if (!membership?.id) {
        this.showErrorSnackbar('Membresía no encontrada');
        return;
      }
      this.membershipToRegenerate = membership;
      this.regenerateLinkError = null;
      this.paymentLinkPreview = null;
      this.regenerateLinkAction = 'keep';
      this.showRegenerateLinkDialog = true;
      this.paymentLinkPreviewLoading = true;
      try {
        const res = await MembershipsService.getPaymentLinkPreview(membership.id);
        this.paymentLinkPreview = res.data;
      } catch (error) {
        this.closeRegenerateLinkDialog();
        this.showErrorSnackbar(
          (error.isApiError && error.message) || 'No se pudo obtener la información de la membresía'
        );
      } finally {
        this.paymentLinkPreviewLoading = false;
      }
    },

    // Confirma la regeneración: según regenerateLinkAction, cancela o mantiene la
    // suscripción anterior, y crea el nuevo link de MercadoPago. Reutiliza el
    // dialog de resultado para mostrar/copiar el link.
    async confirmRegeneratePaymentLink() {
      const membership = this.membershipToRegenerate;
      if (!membership?.id) return;
      this.regenerateLinkSubmitting = true;
      this.regenerateLinkError = null;
      try {
        const action = this.paymentLinkPreview?.hasPreviousPayment
          ? this.regenerateLinkAction
          : 'keep';
        const res = await MembershipsService.regeneratePaymentLink(membership.id, action);
        this.closeRegenerateLinkDialog();
        this.renewalResult = { paymentLink: res.data.paymentLink, regenerated: true };
        this.renewalWarnings = [];
        this.renewalLinkCopied = false;
        this.showRenewalResultDialog = true;

        // El backend puede haber cambiado el estado de la membresía (activa/deactivada
        // → pendiente de pago): refresca la tabla para que no quede desactualizada.
        try {
          await this.refreshMembershipData();
        } catch (refreshError) {
          this.showErrorSnackbar('El link se generó, pero no se pudo refrescar la vista');
        }
      } catch (error) {
        // El error se muestra dentro del dialog para que el usuario pueda reintentar.
        // El detalle técnico del backend (ej. error crudo de MercadoPago) se guarda
        // aparte y se muestra colapsado, para no ensuciar el mensaje principal.
        this.regenerateLinkError = {
          code: error.isApiError ? error.code : null,
          message: (error.isApiError && error.message) || 'No se pudo regenerar el link de pago',
          detail: (error.isApiError && error.detail) || null
        };
        this.showRegenerateErrorDetail = false;
      } finally {
        this.regenerateLinkSubmitting = false;
      }
    },

    closeRegenerateLinkDialog() {
      this.showRegenerateLinkDialog = false;
      this.membershipToRegenerate = null;
      this.regenerateLinkError = null;
      this.showRegenerateErrorDetail = false;
      this.paymentLinkPreview = null;
      this.paymentLinkPreviewLoading = false;
      this.regenerateLinkAction = 'keep';
    },

    // Limpia el estado cuando el dialog se cierra por clic afuera o ESC
    onRenewDialogInput(open) {
      if (!open) this.closeRenewDialog();
    },

    async handleMembershipRenewed(result) {
      this.closeRenewDialog();

      const warnings = result?.warnings || [];
      // Respuestas antiguas sin `mpManaged` se tratan como gestionadas por MP
      const mpManaged = result?.mpManaged !== false;
      // El dialog de resultado se muestra si hay link, advertencias o un cambio de
      // intervalo gestionado por MP; el cambio a plan de planilla no requiere acción
      if (result?.paymentLink || warnings.length > 0 || (result?.intervalChanged && mpManaged)) {
        this.renewalResult = result;
        this.renewalWarnings = warnings;
        this.renewalLinkCopied = false;
        this.showRenewalResultDialog = true;
      } else {
        this.showSuccessSnackbar('Membresía renovada correctamente');
      }

      try {
        this.showOverlay = true;
        await this.refreshMembershipData();
      } catch (error) {
        this.showErrorSnackbar('La renovación se realizó, pero no se pudo refrescar la vista');
      } finally {
        this.showOverlay = false;
      }
    },

    closeRenewalResultDialog() {
      this.showRenewalResultDialog = false;
      this.renewalResult = null;
      this.renewalWarnings = [];
      this.renewalLinkCopied = false;
    },

    async copyRenewalPaymentLink() {
      const link = this.renewalResult && this.renewalResult.paymentLink;
      if (!link) return;
      try {
        await navigator.clipboard.writeText(link);
        this.renewalLinkCopied = true;
      } catch (error) {
        this.showErrorSnackbar('No se pudo copiar el link, cópialo manualmente');
      }
    },

    async updatePetDataLocally() {
      const membershipIndex = this.detailsPlan.findIndex(plan => plan.id === this.editingPet.id);
      if (membershipIndex !== -1) {
        this.detailsPlan[membershipIndex].name = this.editingPet.name;
      }
      
      const petIndex = this.petInfo.findIndex(pet => pet.id === this.editingPet.id);
      if (petIndex !== -1) {
        this.petInfo[petIndex].mascota = this.editingPet.name;
        this.petInfo[petIndex].raza = this.editingPet.breed;
      }
      this.requestUsage.forEach(request => {
        if (request.name === this.editingPet.originalName) {
          request.name = this.editingPet.name;
        }
      });
      this.serviceUsage.forEach(usage => {
        if (usage.name === this.editingPet.originalName) {
          usage.name = this.editingPet.name;
        }
      });
      this.sponsorship.forEach(sponsor => {
        if (sponsor.name === this.editingPet.originalName) {
          sponsor.name = this.editingPet.name;
        }
      });
      if (this.stats[this.editingPet.originalName]) {
        this.stats[this.editingPet.name] = this.stats[this.editingPet.originalName];
        delete this.stats[this.editingPet.originalName];
      }
    }
  }
}
</script>

<style scoped>
/* v-tabs-items trae fondo propio de Vuetify; se deja transparente para que cada pestaña
   use el fondo de la página como el resto de las vistas. */
.vision360-tabs-items {
  background-color: transparent !important;
}
</style>